import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface FridgeRentalRecord {
  id: string;
  firstName: string;
  lastName: string;
  accommodation: string;
}

const FridgeRentals: React.FC = () => {
  const [records, setRecords] = useState<FridgeRentalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFridgeRentals = async () => {
      try {
        setLoading(true);
        setError(null);
        const snapshot = await getDocs(collection(db, 'Registrations'));
        const filtered: FridgeRentalRecord[] = [];
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          // Only include registrations that have been assigned a place (exclude Pending, etc.)
          if (data.status !== 'Assigned') {
            return;
          }
          // Check multiple possible structures for fridge rental
          const hasFridge = (data.financial?.otherFees?.fridge?.requested === true) ||
                           (data.financial?.otherFees?.fridge?.cost > 0) ||
                           (data.otherFees?.fridgeRental === 25) ||
                           (data.optionalFees?.fridgeRental === true);
          
          if (hasFridge) {
            filtered.push({
              id: docSnap.id,
              firstName: data.registrationInfo?.primaryCamperName?.split(' ')[0] || data.firstName || '',
              lastName: data.registrationInfo?.primaryCamperName?.split(' ').slice(1).join(' ') || data.lastName || '',
              accommodation: data.assignedAccommodation?.placeName || data.accommodationFees?.placeName || data.financial?.accommodationFees?.placeName || ''
            });
          }
        });
        setRecords(filtered);
      } catch (err) {
        setError('Failed to load fridge rentals');
      } finally {
        setLoading(false);
      }
    };
    fetchFridgeRentals();
  }, []);

  const exportToCSV = () => {
    const headers = ['First Name', 'Last Name', 'Accommodation', 'Fridge Requested'];
    const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
    const csvRows = [
      headers.join(','),
      ...records.map(record =>
        [escape(record.firstName), escape(record.lastName), escape(record.accommodation), escape('Yes')].join(',')
      ),
    ];
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `fridge-rentals-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div>Loading fridge rentals...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="fridge-rentals-report">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0 }}>Fridge Rentals</h2>
        {records.length > 0 && (
          <button
            style={{
              padding: '8px 16px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.9rem',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#218838')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#28a745')}
            onClick={exportToCSV}
          >
            Export to CSV
          </button>
        )}
      </div>
      <table className="stats-table">
        <thead>
          <tr>
            <th>Primary Camper</th>
            <th>Accommodation</th>
            <th>Fridge Requested</th>
          </tr>
        </thead>
        <tbody>
          {records.map(record => (
            <tr key={record.id}>
              <td>{record.firstName} {record.lastName}</td>
              <td>{record.accommodation}</td>
              <td>Yes</td>
            </tr>
          ))}
        </tbody>
      </table>
      {records.length === 0 && <div>No fridge rentals found.</div>}
    </div>
  );
};

export default FridgeRentals; 